/**
 * The Service categories screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/services/categories` (all optional; a default is never written):
 * - `q`: search on the category name and description.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends categories in no defined order, so
 *   the default is Name ascending.
 */
import type { ServiceCategory } from '@/api/serviceCategories';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const SERVICE_CATEGORIES_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type ServiceCategorySortKey = 'name' | 'description' | 'status';
export const SERVICE_CATEGORY_SORT_KEYS: readonly ServiceCategorySortKey[] = [
  'name',
  'description',
  'status',
];
export const SERVICE_CATEGORY_DEFAULT_SORT: SortState<ServiceCategorySortKey> = {
  key: 'name',
  dir: 'asc',
};

/** Display name for a category (its name, falling back to the id). */
export function categoryName(category: ServiceCategory): string {
  return category.name === '' ? category.id : category.name;
}

/** The lifecycle Status chip label. */
export function categoryStatusLabel(category: ServiceCategory): string {
  if (category.status === 'DRAFT') return 'Draft';
  if (category.status === 'INACTIVE') return 'Inactive';
  return 'Active';
}

/** What a category row shows (and sorts by); no description sorts last. */
export function serviceCategorySortValue(
  category: ServiceCategory,
  key: ServiceCategorySortKey,
): SortValue {
  switch (key) {
    case 'name':
      return categoryName(category);
    case 'description':
      return category.description;
    case 'status':
      return categoryStatusLabel(category);
  }
}

/** The categories search: the name or the description. */
export function serviceCategoryMatches(category: ServiceCategory, query: string): boolean {
  return matchesSearch(query, [categoryName(category), category.description]);
}
